/**
 * Fetches daily Mass readings from USCCB.
 *
 * The USCCB provides the official daily readings for the USA at:
 *   https://bible.usccb.org/bible/readings/MMDDYY.cfm
 *
 * Per USCCB policy: "No permission or fee is needed to display the daily
 * readings on an RSS feed only on a website which does not condition access
 * by users on the users giving anything of value to the website operator."
 *
 * We fetch through a CORS proxy and parse the HTML for reading sections.
 */

import { useQuery } from '@tanstack/react-query';
import { formatUSCCBDate } from '@/lib/liturgical/computus';

const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

export interface USCCBReading {
  label: string;         // e.g. "Reading 1", "Responsorial Psalm", "Gospel"
  reference: string;     // e.g. "Acts 25:13b-21"
  text: string;          // Full reading text
}

export interface USCCBReadingsResult {
  title: string;         // e.g. "Friday of the Seventh Week of Easter"
  lectionary: string;    // e.g. "Lectionary: 301"
  readings: USCCBReading[];
  url: string;           // Direct link to USCCB page
}

/**
 * Extract readable text from an element, inserting spaces wherever a `<br>`
 * tag appears so that sentence boundaries don't collapse into single words.
 */
function getTextWithSpaces(el: Element): string {
  const clone = el.cloneNode(true) as Element;
  clone.querySelectorAll('br').forEach((br) => {
    br.replaceWith(document.createTextNode(' '));
  });
  return clone.textContent?.trim() ?? '';
}

function parseUSCCBHtml(html: string, url: string): USCCBReadingsResult {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Extract title
  const titleEl = doc.querySelector('.b-lectionary .padding h3, .b-lectionary h3');
  const title = titleEl?.textContent?.trim() ?? '';

  // Extract lectionary number
  const lecEl = doc.querySelector('.b-lectionary .padding p, .b-lectionary p');
  const lectionary = lecEl?.textContent?.trim() ?? '';

  // Extract readings - each reading section has a specific structure
  const readings: USCCBReading[] = [];
  const readingBlocks = doc.querySelectorAll('.b-verse');

  readingBlocks.forEach((block) => {
    // The label (e.g. "Reading 1", "Gospel")
    const labelEl = block.querySelector('h4');
    const label = labelEl?.textContent?.trim() ?? '';

    // The reference (e.g. "Acts 25:13b-21")
    const refEl = block.querySelector('.address a, .address');
    const reference = refEl?.textContent?.trim() ?? '';

    // The text content - preserve paragraph breaks and insert spaces for <br>
    const contentEl = block.querySelector('.content-body');
    let text = '';
    if (contentEl) {
      const paras = contentEl.querySelectorAll('p');
      if (paras.length > 0) {
        text = Array.from(paras)
          .map((p) => getTextWithSpaces(p))
          .filter(Boolean)
          .join('\n\n');
      } else {
        text = getTextWithSpaces(contentEl);
      }
    }

    if (label || text) {
      readings.push({ label, reference, text });
    }
  });

  // Fallback parsing if structured parsing didn't find readings
  if (readings.length === 0) {
    const allContent = doc.querySelectorAll('.main-content .field--type-text-long, .b-verse');
    const labels = ['Reading 1', 'Responsorial Psalm', 'Reading 2', 'Alleluia', 'Gospel'];
    let labelIdx = 0;

    allContent.forEach((el) => {
      const headingEl = el.querySelector('h4, h3');
      const heading = headingEl?.textContent?.trim();

      // Find the reference link
      const refLink = el.querySelector('a[href*="bible.usccb.org/bible"]');
      const ref = refLink?.textContent?.trim() ?? '';

      // Get text excluding the heading and reference
      const textParts: string[] = [];
      el.querySelectorAll('p, .content-body').forEach((p) => {
        const t = getTextWithSpaces(p);
        if (t && t !== heading && t !== ref) {
          textParts.push(t);
        }
      });
      const text = textParts.join('\n\n');

      if (text) {
        readings.push({
          label: heading ?? labels[labelIdx] ?? `Reading ${labelIdx + 1}`,
          reference: ref,
          text,
        });
        labelIdx++;
      }
    });
  }

  return { title, lectionary, readings, url };
}

/**
 * Hook to fetch USCCB daily readings for a given date.
 */
export function useUSCCBReadings(date: Date) {
  const dateStr = formatUSCCBDate(date);
  const url = `https://bible.usccb.org/bible/readings/${dateStr}.cfm`;

  return useQuery<USCCBReadingsResult>({
    queryKey: ['usccb-readings', dateStr],
    queryFn: async ({ signal }) => {
      const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl, { signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch readings: ${response.statusText}`);
      }
      const html = await response.text();
      return parseUSCCBHtml(html, url);
    },
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
    gcTime: 7 * 24 * 60 * 60 * 1000, // Keep in cache for a week
    retry: 2,
  });
}
